import React from "react";
import { CheckCircle2, Hammer, ShieldCheck, Wrench } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const pillars = [
  { icon: ShieldCheck, title: "Quality Materials", text: "We use only top-grade materials from trusted suppliers." },
  { icon: Hammer, title: "Expert Craftsmanship", text: "Skilled crews with decades of hands-on remodeling experience." },
  { icon: Wrench, title: "Custom Solutions", text: "Every project tailored to your home, your style, your budget." },
];

const checkpoints = [
  "Free on-site estimates",
  "Fixed, transparent pricing",
  "Licensed & fully insured",
  "Clean, respectful job sites",
  "On-time project delivery",
  "Satisfaction guaranteed",
];

export default function About() {
  const { site } = useSite();

  return (
    <section
      data-testid="about-section"
      id="about"
      className="bg-white py-20 lg:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">Why Choose Us</span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold uppercase leading-tight tracking-tight">
              {site?.about_title || "Why homeowners trust J&S"}
            </h2>
            <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
              {site?.about_text}
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {checkpoints.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-neutral-800">
                  <CheckCircle2 size={20} className="text-[#DC2626] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-sm">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-0 border-t border-l border-neutral-200">
            {pillars.map((p, i) => (
              <div
                key={i}
                data-testid={`about-pillar-${i}`}
                className="border-r border-b border-neutral-200 p-8 flex flex-col hover:bg-neutral-950 hover:text-white transition-colors group cursor-default"
              >
                <div className="w-12 h-12 bg-[#DC2626] flex items-center justify-center mb-6">
                  <p.icon size={22} className="text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-neutral-600 group-hover:text-neutral-300 transition-colors">
                  {p.text}
                </p>
              </div>
            ))}

            {/* Stats bar */}
            <div className="sm:col-span-3 border-r border-b border-neutral-200 bg-neutral-950 text-white p-8 grid grid-cols-3 gap-4">
              {[
                { n: "10+", l: "Years experience" },
                { n: "500+", l: "Projects completed" },
                { n: "100%", l: "Satisfaction" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="font-heading text-3xl sm:text-4xl font-bold text-[#DC2626]">
                    {s.n}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400 mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
